/**
 * Support-Anfragen-Verwaltung (Admin).
 *
 * Juni 2026 — Eingehende In-App-Support-Anfragen (via Hilfe-&-Feedback-Pill)
 * werden hier gelistet und mit Status verwaltet (new / in_progress / done).
 *
 * Endpoints:
 *   GET   /api/admin/support-requests?status=
 *   PATCH /api/admin/support-requests/:id  { status }
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { RefreshCw, Mail, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';

const STATUS_META = {
  new: { label: 'Neu', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  in_progress: { label: 'In Bearbeitung', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  done: { label: 'Erledigt', cls: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
};

const FILTERS = [
  { value: 'all', label: 'Alle' },
  { value: 'new', label: 'Neu' },
  { value: 'in_progress', label: 'In Bearbeitung' },
  { value: 'done', label: 'Erledigt' },
];

const fmtDate = (iso) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

export default function SupportRequestsAdminPanel() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, in_progress: 0, done: 0, total: 0 });
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [notifyUser, setNotifyUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (search.trim()) params.search = search.trim();
      const res = await api.get('/admin/support-requests', { params });
      setItems(res?.data?.items || []);
      setCounts(res?.data?.counts || { new: 0, in_progress: 0, done: 0, total: 0 });
    } catch (e) {
      toast.error('Support-Anfragen konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => {
    const t = setTimeout(load, 300);   // Debounce für die Suche
    return () => clearTimeout(t);
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const res = await api.patch(`/admin/support-requests/${id}`, { status, notify_user: notifyUser });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success(
        `Status auf „${STATUS_META[status].label}" gesetzt.` +
        (res.data?.notified ? " Nutzer per E-Mail informiert." : "")
      );
      // Counts neu laden (leichtgewichtig)
      load();
    } catch (e) {
      toast.error('Status konnte nicht aktualisiert werden.');
    }
  };

  return (
    <div data-testid="admin-support-requests-panel">
      {/* KPI + Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Support-Anfragen
          </h2>
          <p className="text-sm text-zinc-500">
            {counts.total} gesamt · {counts.new} neu · {counts.in_progress} in Bearbeitung · {counts.done} erledigt
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs font-medium text-zinc-600 cursor-pointer select-none mr-1" data-testid="support-notify-toggle">
            <input
              type="checkbox"
              checked={notifyUser}
              onChange={(e) => setNotifyUser(e.target.checked)}
              className="h-4 w-4 rounded border-zinc-300 accent-emerald-600"
              data-testid="support-notify-checkbox"
            />
            Nutzer bei Statuswechsel benachrichtigen
          </label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ticket, E-Mail, Betreff…"
              className="pl-8 h-9 w-56"
              data-testid="support-search-input"
            />
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="support-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Filter-Pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            data-testid={`support-filter-${f.value}`}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              filter === f.value
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Liste */}
      {items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-zinc-500" data-testid="support-empty">
            <Mail className="h-8 w-8 mx-auto mb-3 text-zinc-300" />
            Keine Support-Anfragen{filter !== 'all' ? ' mit diesem Status' : ''}.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const meta = STATUS_META[it.status] || STATUS_META.new;
            const isOpen = expanded === it.id;
            return (
              <Card key={it.id} data-testid={`support-row-${it.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${meta.cls}`}>
                          {meta.label}
                        </span>
                        {it.ticket_number && (
                          <span className="px-2 py-0.5 rounded-md text-xs font-mono font-semibold bg-zinc-100 text-zinc-700 border border-zinc-200" data-testid={`support-ticket-${it.id}`}>
                            {it.ticket_number}
                          </span>
                        )}
                        <span className="font-semibold text-zinc-900 truncate">{it.subject || 'Support-Anfrage'}</span>
                      </div>
                      <div className="text-sm text-zinc-500 mt-1">
                        <a href={`mailto:${it.user_email}`} className="text-emerald-600 hover:underline">
                          {it.user_email}
                        </a>
                        {' · '}{it.user_plan || 'free'} · {it.user_country || 'DE'} · {fmtDate(it.created_at)}
                      </div>
                      <p className={`text-sm text-zinc-700 mt-2 whitespace-pre-wrap ${isOpen ? '' : 'line-clamp-2'}`}>
                        {it.message}
                      </p>
                      {(it.message?.length > 140 || it.page_url) && (
                        <button
                          onClick={() => setExpanded(isOpen ? null : it.id)}
                          className="mt-1 text-xs text-zinc-500 hover:text-zinc-700 inline-flex items-center gap-1"
                          data-testid={`support-toggle-${it.id}`}
                        >
                          {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {isOpen ? 'Weniger' : 'Mehr'}
                        </button>
                      )}
                      {isOpen && (
                        <div className="mt-2 text-xs text-zinc-400 space-y-0.5">
                          <div>Seite: {it.page_url || '–'}</div>
                          <div>Browser: {it.browser_info || '–'}</div>
                          <div>Auflösung: {it.viewport_size || '–'}</div>
                        </div>
                      )}
                    </div>

                    {/* Status-Aktionen */}
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {Object.keys(STATUS_META).map((s) => (
                        <button
                          key={s}
                          disabled={it.status === s}
                          onClick={() => updateStatus(it.id, s)}
                          data-testid={`support-set-${s}-${it.id}`}
                          className={`px-3 py-1 rounded-md text-xs font-medium border transition-colors ${
                            it.status === s
                              ? `${STATUS_META[s].cls} cursor-default`
                              : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
                          }`}
                        >
                          {STATUS_META[s].label}
                        </button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
